// TriggerForceGraphs.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useContext, useEffect, useMemo } from 'react';
import type { TFunction } from 'i18next';
import MultiLineChart from '../../../../../../../components/multi-line-chart/multi-line-chart';
import BarDistributionChart from '../graphs/bar-distribution-chart';
import BrushGraphs from '../../../../../../../../app/components/brush-graphs/brush-graphs';
import EmptyState from '../graphs/empty-grphs';
import { AvailableResourceActions, can, Role, ROLES } from '../../../utils';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { DeviceModelType } from 'apps/marsinet/src/app/models/devicesModels';

type TriggerAvg = {
  averageStepTimeLeftLeg: {
    dataBarDistribution: any;
    optionsBarDistribution: any;
  };
  averageStepTimeRightLeg: {
    dataBarDistribution: any;
    optionsBarDistribution: any;
  };
} | null;

type TriggerGraphsProps = {
  t: TFunction<'translation'>;
  userRole?: Role;
  deviceModel: DeviceModelType | null;

  // series completas (sin filtrar)
  left: any[] | null; // leftJointThresholdAndEffort
  right: any[] | null; // rightJointThresholdAndEffort
  averageStepTime: TriggerAvg;
  rom: any[] | null; // traemos el rom
  // brush controlado desde el padre
  startIndex: number;
  endIndex: number;
  onBrushChange: (r: { startIndex: number; endIndex: number }) => void;
  onBaseLenChange: (len: number) => void;
};

// Claves ROM que corresponden a cada lado
const ROM_KEYS_LEFT = ['motorsAngRef2', 'motorsAngRef3'];
const ROM_KEYS_RIGHT = ['motorsAngRef6', 'motorsAngRef7'];
const SCALE_FACTOR_EXP = 50;

function scaleEffort(value: any, isExplorerModel: boolean) {
  if (value == null) return null;
  return isExplorerModel ? value / SCALE_FACTOR_EXP : value;
}


function mergeWithRom(points: any[], romByTimestamp: Map<string, any>, romKeys: string[]) {
  return points.map((point) => { //la lista de puntos de fuerza y umbral de una pierna
    const romPoint = romByTimestamp.get(point.timestamp); //Mira la hora del punto de fuerza y obtiene los valores de ROM correspondientes
    const romValues: Record<string, any> = {};
    romKeys.forEach((key) => {
      romValues[key] = romPoint ? (romPoint[key] ?? null) : null;
    });
    return { ...point, ...romValues }; //La gráfica nunca recibe undefined, siempre un número o null, y con null simplemente no pinta ese punto.
  });
}

const noop: (loading: boolean) => void = () => {
  /*Intentionally empty*/
};

function hasValidData(d: any) {
  if (!d) return false;
  if (Array.isArray(d)) return d.length > 0;
  if (typeof d === 'object') return Object.keys(d).length > 0;
  return false;
}

function TriggerGraphImpl(props: Readonly<TriggerGraphsProps>) {
  const { role } = useContext(MarsinetContext);


  const base = props.left?.length ? props.left : (props.right ?? []);


  // Roles que ven "ROM" en vez de "Referencia" en la leyenda
  const showRomLabels =
    !!role && role !== ROLES.MARSI && can(role, AvailableResourceActions.visualization.view_rom_labels);


  const romLabelsLeft = useMemo(
    () =>
      showRomLabels
        ? {
          motorsAngRef2: props.t('pages.patients.viewPatient.sessions.romHipLeft'),  // ROM CI
          motorsAngRef3: props.t('pages.patients.viewPatient.sessions.romKneeLeft'), // ROM RI
        }
        : undefined,
    [showRomLabels, props.t]
  );

  const romLabelsRight = useMemo(
    () =>
      showRomLabels
        ? {
          motorsAngRef6: props.t('pages.patients.viewPatient.sessions.romHipRight'),  // ROM CD
          motorsAngRef7: props.t('pages.patients.viewPatient.sessions.romKneeRight'), // ROM RD
        }
        : undefined,
    [showRomLabels, props.t]
  );


  useEffect(() => {
    props.onBaseLenChange(base.length || 0);
  }, [base.length, props.onBaseLenChange]);

  const isExplorerModel =
    props.deviceModel === DeviceModelType.EXPLORER_PERSONAL ||
    props.deviceModel === DeviceModelType.EXPLORER_CLINICO;

  // Índice de ROM por timestamp, para buscar rápido en el merge
  const romByTimestamp = useMemo(() => {
    const map = new Map<string, any>();
    (props.rom ?? []).forEach((point) => {
      map.set(point.timestamp, point);
    });
    return map;
  }, [props.rom]);



  //Guardamos en esta variable los datos que le vamos a dar a la gráfica de la pierna izquierda. No son los datos "en bruto" 
  // tal como llegan del backend — es una versión ya recortada y ajustada, lista para pintar.
  const filteredLeft = useMemo(() => {
    //Coge los datos de fuerza y umbral de la pierna izquierda. Si no hay, usa una lista vacía para que nada falle.
    const src = props.left ?? [];
    const sliced = src.slice(props.startIndex, props.endIndex + 1);
    //A cada punto del tramo le pega los ángulos de cadera y rodilla izquierdas de su misma hora
    const merged = mergeWithRom(sliced, romByTimestamp, ROM_KEYS_LEFT);
    // Los umbrales se dejan tal cual (con su signo).
    // La fuerza se escala para verla en el mismo eje que el umbral, conservando el signo.
    return merged.map((point) => ({
      ...point,
      intentionEffortHipLeft: scaleEffort(
        point.intentionEffortHipLeft,
        isExplorerModel
      ),
      intentionEffortKneeLeft: scaleEffort(
        point.intentionEffortKneeLeft,
        isExplorerModel
      ),
    }));
  }, [props.left, props.startIndex, props.endIndex, romByTimestamp, isExplorerModel]);


  const filteredRight = useMemo(() => {
    const src = props.right ?? [];
    const sliced = src.slice(props.startIndex, props.endIndex + 1);
    const merged = mergeWithRom(sliced, romByTimestamp, ROM_KEYS_RIGHT);

    return merged.map((point) => ({
      ...point,
      intentionEffortHipRight: scaleEffort(
        point.intentionEffortHipRight,
        isExplorerModel
      ),
      intentionEffortKneeRight: scaleEffort(
        point.intentionEffortKneeRight,
        isExplorerModel
      ),
    }));
  }, [props.right, props.startIndex, props.endIndex, romByTimestamp, isExplorerModel]);


  const hasAvg =
    !!props.averageStepTime &&
    hasValidData(
      props.averageStepTime.averageStepTimeLeftLeg?.dataBarDistribution?.labels
    ) &&
    hasValidData(
      props.averageStepTime.averageStepTimeRightLeg?.dataBarDistribution?.labels
    );

  const showLines =
    hasValidData(filteredLeft) || hasValidData(filteredRight) || hasAvg;

  if (!showLines) {
    return (
      <EmptyState icon="pi pi-sliders-h" title={props.t('emptyMessage')} />
    );
  }

  return (
    <>
      {hasAvg && (
        <div className="flex flex-wrap gap-4">
          <div className="w-full sm:w-1/2 lg:w-1/3 min-w-[300px] h-[400px] sm:h-[500px] p-4 bg-white shadow-lg rounded-lg border border-gray-300">
            <BarDistributionChart
              data={
                props.averageStepTime!.averageStepTimeLeftLeg
                  .dataBarDistribution
              }
              options={
                props.averageStepTime!.averageStepTimeLeftLeg
                  .optionsBarDistribution
              }
            />
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/3 min-w-[300px] h-[400px] sm:h-[500px] p-4 bg-white shadow-lg rounded-lg border border-gray-300">
            <BarDistributionChart
              data={
                props.averageStepTime!.averageStepTimeRightLeg
                  .dataBarDistribution
              }
              options={
                props.averageStepTime!.averageStepTimeRightLeg
                  .optionsBarDistribution
              }
            />
          </div>
        </div>
      )}

      {/* Brush */}
      <div className="sticky z-1" style={{ top: '90px' }}>
        <BrushGraphs
          data={base}
          startIndex={props.startIndex}
          endIndex={props.endIndex}
          onBrushChange={props.onBrushChange}
        />
      </div>

      <div className="col-12 flex flex-column gap-2 mb-2" />

      {hasValidData(filteredLeft) && (
        <MultiLineChart
          data={filteredLeft}
          seriesLabels={romLabelsLeft}
          title={props.t(
            'pages.patients.viewPatient.sessions.leftSideEffortAndThreshold'
          )}
          setLoading={noop}
          chartOptions={{
            yAxisTitle: props.t('pages.patients.viewPatient.threshold_force'),
            secondaryYAxisTitle: props.t('pages.patients.viewPatient.anglesTitle'),
          }}
          secondaryAxisKeys={ROM_KEYS_LEFT}
          defaultHiddenKeys={ROM_KEYS_LEFT}
        />
      )}

      {hasValidData(filteredRight) && (
        <MultiLineChart
          data={filteredRight}
          seriesLabels={romLabelsRight}
          title={props.t(
            'pages.patients.viewPatient.sessions.rightSideEffortAndThreshold'
          )}
          setLoading={noop}
          chartOptions={{
            yAxisTitle: props.t('pages.patients.viewPatient.threshold_force'),
            secondaryYAxisTitle: props.t('pages.patients.viewPatient.anglesTitle'),
          }}
          secondaryAxisKeys={ROM_KEYS_RIGHT}
          defaultHiddenKeys={ROM_KEYS_RIGHT}
        />
      )}
    </>
  );
}

export default memo(TriggerGraphImpl);
